import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const LOAD_WATCH_TIMEOUT = 20000;

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [counter] - The image counter.
 * @property {HTMLButtonElement} [previous] - Previous control.
 * @property {HTMLButtonElement} [next] - Next control.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #activeIndex = 0;

  /** @type {HTMLElement | null} */
  #focusRestore = null;

  /** @type {number} */
  #swipeStartX = 0;

  /** @type {number} */
  #swipeStartY = 0;

  /** @type {boolean} */
  #swipeTracking = false;

  /** @type {AbortController | null} */
  #pdpController = null;

  /** @type {number | null} */
  #idleWarmId = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #variantTimer = null;

  /** @type {boolean} */
  #warming = false;

  /** @type {Promise<void> | null} */
  #warmPromise = null;

  /** @type {Map<string, { key: string, url: string }>} */
  #snapshots = new Map();

  /** Bumped on every variant change so captures started earlier are discarded. */
  #snapshotGeneration = 0;

  /** @type {Map<string, { generation: number, promise: Promise<void> }>} */
  #snapshotsInFlight = new Map();

  /** @type {AbortController | null} */
  #loadWatch = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, { passive: true });

    if (!this.#useExclusiveItems) return;

    this.#pdpController?.abort();
    this.#pdpController = new AbortController();
    const { signal } = this.#pdpController;
    const gallery = this.closest('media-gallery');

    gallery?.addEventListener('pointerenter', this.#onWarmIntent, { once: true, passive: true, signal });
    gallery?.addEventListener('touchstart', this.#onWarmIntent, { once: true, passive: true, signal });
    this.addEventListener('zoomchange', this.#onZoomChange, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantInvalidate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantInvalidate, { signal });

    const scheduleIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    this.#idleWarmId = scheduleIdle(() => this.warm());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('touchstart', this.#handleSwipeStart);
    this.refs.dialog.removeEventListener('touchend', this.#handleSwipeEnd);
    this.#pdpController?.abort();
    this.#pdpController = null;
    this.#loadWatch?.abort();
    this.#loadWatch = null;
    if (this.#idleWarmId != null) {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(this.#idleWarmId);
      else clearTimeout(this.#idleWarmId);
      this.#idleWarmId = null;
    }
    if (this.#variantTimer) clearTimeout(this.#variantTimer);
    this.#clearSnapshots();
  }

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, thumbnails } = this.refs;
    const media = this.#items;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];
    const opener = event.target instanceof Element ? event.target.closest('li,slideshow-slide,button') : null;
    this.#focusRestore = opener instanceof HTMLElement ? opener : null;

    const warmPromise = this.#useExclusiveItems ? this.warm() : Promise.resolve();
    if (this.#useExclusiveItems) {
      this.#primeAround(index);
      // Room photos are large and lazy; start them on open so swiping to an in-room slide isn't a long wait.
      media.forEach((item, i) => {
        if (i !== index && item.classList.contains('product-media-container--in-situ')) this.#primeItem(i, 'low');
      });
    }

    const snapshotReady = this.#useExclusiveItems && this.#hasSnapshot(targetImage);
    const photoReady =
      !this.#useExclusiveItems || this.#isMockupItem(targetImage) || (await this.#whenItemReady(targetImage, 150));

    const open = () => {
      dialog.showModal();
      this.#setActive(index, { behavior: 'instant', announce: false });

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      const closeButton = this.querySelector('.dialog-zoomed-gallery__close-button');
      if (closeButton instanceof HTMLElement) closeButton.focus();

      this.#relayoutMockups();
      if (this.#useExclusiveItems && !snapshotReady) {
        warmPromise.then(() => {
          this.#applySnapshot(targetImage);
          if (!this.#hasSnapshot(targetImage)) this.#snapshotVisibleItem(targetImage);
          this.#relayoutMockups();
        });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const skipTransition =
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      prefersReducedMotion() ||
      !sourceImage ||
      !targetImage ||
      !photoReady ||
      (this.#useExclusiveItems && this.#isMockupItem(targetImage) && !snapshotReady);

    if (skipTransition) {
      open();
      return;
    }

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * @param {HTMLElement} mediaContainer
   */
  #loadHighResolutionImage(mediaContainer) {
    if (this.#useExclusiveItems) return false;
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  handleScroll = debounce(async () => {
    const media = this.#items;
    const { thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#activeIndex = activeIndex;
    this.#updateChrome();

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex, this.#slideIndexFor(mostVisibleElement, activeIndex)));
  }, 50);

  async close() {
    const { dialog } = this.refs;
    const media = this.#items;

    if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) return this.closeDialog();

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slideIndex = this.#slideIndexFor(mostVisibleElement, activeIndex);

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[slideIndex] : mediaGallery?.media?.[slideIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
    const restore = this.#focusRestore;
    this.#focusRestore = null;
    if (restore instanceof HTMLElement) {
      restore.focus({ preventScroll: true });
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    // The delegated `on:click` proxy reports the dialog as `target`; the path keeps the element actually clicked.
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;
    if (
      target.closest(
        'drag-zoom-wrapper, .lightbox-zoom-stage, .product-media, .dialog-zoomed-gallery__nav, .dialog-zoomed-gallery__zoom-controls, .dialog-zoomed-gallery__close-button, .dialog-thumbnails-list__thumbnail'
      )
    ) {
      return;
    }
    this.close();
  }

  next() {
    this.#setActive(this.#activeIndex + 1);
  }

  previous() {
    this.#setActive(this.#activeIndex - 1);
  }

  zoomIn() {
    this.#currentWrapper()?.zoomIn();
  }

  zoomOut() {
    this.#currentWrapper()?.zoomOut();
  }

  /**
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const media = this.#items;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index
   * @param {{ behavior?: ScrollBehavior }} [options]
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.#setActive(index, options);
  }

  /**
   * @param {number} index
   * @param {{ behavior?: ScrollBehavior, announce?: boolean }} [options]
   */
  #setActive(index, options = {}) {
    const media = this.#items;
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    if (isNaN(index) || index < 0 || index >= media.length) return;

    const { thumbnails } = this.refs;
    const previous = media[this.#activeIndex];
    if (previous && previous !== media[index]) {
      const wrapper = previous.querySelector('drag-zoom-wrapper');
      wrapper?.reset?.();
    }

    this.#activeIndex = index;
    const targetThumbnail = thumbnails.children[index];
    const targetImage = media[index];

    media.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior || 'smooth',
        block: 'center',
        inline: 'center',
      });
    }

    if (targetImage && !this.#useExclusiveItems) {
      targetImage.scrollIntoView({
        behavior: options.behavior || 'smooth',
      });
    }

    if (targetImage) this.#loadHighResolutionImage(targetImage);
    if (this.#useExclusiveItems) this.#primeAround(index);
    this.#watchItemLoad(targetImage);
    this.#updateChrome();
    this.#relayoutMockups();

    if (options.announce !== false && targetImage) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(index, this.#slideIndexFor(targetImage, index)));
    }
  }

  get #items() {
    const media = this.refs.media;
    if (Array.isArray(media)) return media;
    return media ? [media] : [];
  }

  get #useExclusiveItems() {
    return this.hasAttribute('data-pdp-lightbox');
  }

  #updateChrome() {
    const total = this.#items.length;
    const current = this.#activeIndex + 1;

    if (this.refs.previous) {
      this.refs.previous.disabled = this.#activeIndex <= 0;
      this.refs.previous.setAttribute('aria-disabled', String(this.#activeIndex <= 0));
    }
    if (this.refs.next) {
      this.refs.next.disabled = this.#activeIndex >= total - 1;
      this.refs.next.setAttribute('aria-disabled', String(this.#activeIndex >= total - 1));
    }
    if (this.refs.counter) {
      this.refs.counter.textContent = `${current} / ${total}`;
    }
  }

  #relayoutMockups() {
    requestAnimationFrame(() => {
      this.querySelectorAll('live-product-mockup').forEach((node) => {
        if (typeof node.relayout === 'function') node.relayout();
      });
    });
  }

  #onWarmIntent = () => {
    this.warm();
  };

  #onVariantInvalidate = () => {
    if (!this.#useExclusiveItems) return;
    this.#snapshotGeneration++;
    if (this.#variantTimer) clearTimeout(this.#variantTimer);
    this.#variantTimer = setTimeout(() => {
      this.#clearSnapshots();
      this.#items.forEach((item) => this.#showFallback(item));
      this.warm({ force: true });
    }, 400);
  };

  /**
   * @param {Event} event
   */
  #onZoomChange = (event) => {
    const scale = event instanceof CustomEvent ? Number(event.detail?.scale) : NaN;
    if (!Number.isFinite(scale) || scale < 1.5) return;
    const item = this.#items[this.#activeIndex];
    if (!item) return;

    item.querySelectorAll('img[data-hires-src]').forEach((img) => {
      if (!(img instanceof HTMLImageElement) || img.dataset.hiresLoaded === 'true') return;
      const url = img.getAttribute('data-hires-src');
      if (!url) return;
      img.dataset.hiresLoaded = 'true';
      swapWhenLoaded(img, { src: url });
    });

    // Raising `sizes` lets the browser pick a larger srcset candidate for the zoomed view.
    const zoomVw = Math.ceil(scale * 2) * 50;
    item.querySelectorAll('img.product-media__image').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      const current = Number(img.dataset.zoomVw || 100);
      if (zoomVw <= current) return;
      img.dataset.zoomVw = String(zoomVw);
      const sizes = `${zoomVw}vw`;
      const srcset = img.dataset.fullSrcset || img.getAttribute('srcset');
      if (!srcset || img.dataset.upgrading === 'true') {
        img.sizes = sizes;
        return;
      }
      swapWhenLoaded(img, { srcset, sizes });
    });
  };

  /**
   * @param {{ force?: boolean }} [options]
   * @returns {Promise<void>}
   */
  warm(options = {}) {
    if (!this.#useExclusiveItems) return Promise.resolve();
    if (this.#warmPromise && !options.force) return this.#warmPromise;

    this.#warmPromise = this.#warmAll(options.force === true).finally(() => {
      this.#warming = false;
    });
    return this.#warmPromise;
  }

  /**
   * @param {boolean} force
   */
  async #warmAll(force) {
    if (this.#warming && !force) return;
    this.#warming = true;

    const kinds = ['live-mockup'];
    for (const kind of kinds) {
      const item = this.querySelector(`[data-snapshot-for="${kind}"]`)?.closest('.product-media-container');
      if (!(item instanceof HTMLElement)) continue;
      await this.#snapshotKind(kind, force);
      this.#applySnapshot(item);
    }
  }

  /**
   * Overlapping requests for the same variant share one capture.
   * @param {string} kind
   * @param {boolean} force
   * @returns {Promise<void>}
   */
  #snapshotKind(kind, force) {
    const generation = this.#snapshotGeneration;
    const inFlight = this.#snapshotsInFlight.get(kind);
    if (inFlight && inFlight.generation === generation) return inFlight.promise;

    const promise = this.#captureKind(kind, force, generation).finally(() => {
      if (this.#snapshotsInFlight.get(kind)?.promise === promise) this.#snapshotsInFlight.delete(kind);
    });
    this.#snapshotsInFlight.set(kind, { generation, promise });
    return promise;
  }

  /**
   * @param {string} kind
   * @param {string} key
   * @param {number} generation
   * @param {string} url
   * @returns {boolean} whether the capture was kept
   */
  #storeSnapshot(kind, key, generation, url) {
    const source = this.#gallerySource(kind);
    const current = source ? this.#variantKey(source, kind) : '';
    if (generation !== this.#snapshotGeneration || key !== current) {
      URL.revokeObjectURL(url);
      return false;
    }
    const cached = this.#snapshots.get(kind);
    if (cached?.url && cached.url !== url) URL.revokeObjectURL(cached.url);
    this.#snapshots.set(kind, { key, url });
    return true;
  }

  /**
   * @param {string} kind
   * @param {boolean} force
   * @param {number} generation
   */
  async #captureKind(kind, force, generation) {
    const source = this.#gallerySource(kind);
    if (!source) return;

    const key = this.#variantKey(source, kind);
    const cached = this.#snapshots.get(kind);
    if (!force && cached?.key === key && cached.url) return;

    if (
      source instanceof HTMLElement &&
      source.matches('slideshow-slide') &&
      source.getAttribute('aria-hidden') === 'true' &&
      !force
    ) {
      return;
    }

    const slide = source.closest('slideshow-slide');
    const previousVisibility = slide instanceof HTMLElement ? slide.style.contentVisibility : '';
    if (slide instanceof HTMLElement) slide.style.contentVisibility = 'visible';

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    try {
      if (!(await this.#waitSourceReady(source))) return;
      const { snapshotMockup } = await import('@theme/mockup-snapshot');
      const url = await snapshotMockup(source);
      if (!url) return;
      this.#storeSnapshot(kind, key, generation, url);
    } catch {
      // Keep the live HTML copy as the lightbox content.
    } finally {
      if (slide instanceof HTMLElement) slide.style.contentVisibility = previousVisibility;
    }
  }

  /**
   * @param {string} kind
   * @returns {HTMLElement | null}
   */
  #gallerySource(kind) {
    const gallery = this.closest('media-gallery');
    if (!gallery) return null;
    if (kind === 'live-mockup') {
      return gallery.querySelector('slideshow-slide[slide-id="live-mockup"] live-product-mockup');
    }
    if (kind === 'in-situ') {
      return gallery.querySelector('slideshow-slide[slide-id="in-situ"]');
    }
    if (kind === 'in-situ-2') {
      return gallery.querySelector('slideshow-slide[slide-id="in-situ-2"]');
    }
    return null;
  }

  /**
   * @param {HTMLElement} source
   * @param {string} kind
   */
  #variantKey(source, kind) {
    const mockup =
      source instanceof HTMLElement && source.matches('live-product-mockup')
        ? source
        : source.querySelector('live-product-mockup');
    // The *-key attributes track the live selection; data-size / data-frame only hold the server-rendered variant.
    const size = mockup?.getAttribute('data-size-key') || mockup?.getAttribute('data-size') || '';
    const frame = mockup?.getAttribute('data-frame-key') || mockup?.getAttribute('data-frame') || '';
    const mat = mockup?.getAttribute('data-mat') || '';
    return `${kind}:${size}:${frame}:${mat}`;
  }

  /**
   * @param {HTMLElement} source
   * @returns {Promise<boolean>} false when the frame can't be captured with all four sides
   */
  async #waitSourceReady(source) {
    const mockup =
      source instanceof HTMLElement && source.matches('live-product-mockup')
        ? source
        : source.querySelector('live-product-mockup');
    const art = mockup?.querySelector('.live-mockup__art');
    const room = source.querySelector?.('.in-situ__bg') || (source.classList?.contains('in-situ') ? source.querySelector('img') : null);
    /** @type {Promise<boolean>} */
    const rails =
      mockup && typeof (/** @type {any} */ (mockup).whenRailsReady) === 'function'
        ? /** @type {any} */ (mockup).whenRailsReady()
        : Promise.resolve(true);
    const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 10000));
    const ready = Promise.all([this.#whenDecoded(art), this.#whenDecoded(room), rails]).then(
      ([, , railsOk]) => railsOk !== false
    );
    const complete = await Promise.race([ready, timeout]);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    return complete === true;
  }

  /**
   * @param {Element | null | undefined} img
   */
  #whenDecoded(img) {
    if (!(img instanceof HTMLImageElement) || !img.src) return Promise.resolve();
    const finish = () => (typeof img.decode === 'function' ? img.decode().then(() => {}, () => {}) : Promise.resolve());
    if (img.complete && img.naturalWidth > 0) return finish();
    return new Promise((resolve) => {
      img.addEventListener('load', () => finish().then(resolve, resolve), { once: true });
      img.addEventListener('error', () => resolve(), { once: true });
    });
  }

  /**
   * @param {HTMLElement | undefined} item
   */
  #applySnapshot(item) {
    if (!item) return;
    const img = item.querySelector('.lightbox-snapshot');
    if (!(img instanceof HTMLImageElement)) return;
    const kind = img.dataset.snapshotFor;
    if (!kind || kind !== 'live-mockup') {
      this.#showFallback(item);
      return;
    }
    const cached = this.#snapshots.get(kind);
    const source = this.#gallerySource(kind);
    const currentKey = source ? this.#variantKey(source, kind) : '';
    if (cached?.url && cached.key === currentKey) {
      img.src = cached.url;
      img.hidden = false;
      img.removeAttribute('hidden');
      item.classList.add('has-snapshot');
      const fallback = item.querySelector('.lightbox-live-fallback');
      if (fallback instanceof HTMLElement) fallback.hidden = true;
      if (item.classList.contains('is-active')) this.#watchItemLoad(item);
      return;
    }
    this.#showFallback(item);
  }

  /**
   * @param {HTMLElement} item
   */
  #showFallback(item) {
    item.classList.remove('has-snapshot');
    const img = item.querySelector('.lightbox-snapshot');
    if (img instanceof HTMLImageElement) img.hidden = true;
    const fallback = item.querySelector('.lightbox-live-fallback');
    if (fallback instanceof HTMLElement) fallback.hidden = false;
  }

  /**
   * @param {HTMLElement | undefined} item
   */
  #hasSnapshot(item) {
    if (!item) return false;
    const img = item.querySelector('.lightbox-snapshot');
    const kind = img instanceof HTMLImageElement ? img.dataset.snapshotFor : '';
    if (!kind) return false;
    const cached = this.#snapshots.get(kind);
    const source = this.#gallerySource(kind);
    return Boolean(cached?.url && source && cached.key === this.#variantKey(source, kind));
  }

  /**
   * @param {HTMLElement | undefined} item
   */
  #isMockupItem(item) {
    return Boolean(item?.querySelector('.lightbox-snapshot'));
  }

  /**
   * @param {HTMLElement | undefined} item
   */
  async #snapshotVisibleItem(item) {
    if (!item) return;
    const img = item.querySelector('.lightbox-snapshot');
    const kind = img instanceof HTMLImageElement ? img.dataset.snapshotFor : '';
    if (kind !== 'live-mockup') return;

    await this.#snapshotKind(kind, true);
    if (this.#hasSnapshot(item)) {
      this.#applySnapshot(item);
      return;
    }

    const fallbackRoot = item.querySelector('.lightbox-live-fallback');
    if (!(fallbackRoot instanceof HTMLElement)) return;
    this.#showFallback(item);
    this.#relayoutMockups();
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const captureEl = item.querySelector('.lightbox-zoom-stage') || fallbackRoot;
    const generation = this.#snapshotGeneration;
    try {
      if (!(await this.#waitSourceReady(captureEl))) return;
      const { snapshotMockup } = await import('@theme/mockup-snapshot');
      const url = await snapshotMockup(captureEl);
      if (!url) return;
      if (this.#storeSnapshot(kind, this.#variantKey(captureEl, kind), generation, url)) this.#applySnapshot(item);
    } catch {
      this.#showFallback(item);
    }
  }

  #clearSnapshots() {
    this.#snapshotGeneration++;
    this.#snapshots.forEach((entry) => {
      if (entry.url) URL.revokeObjectURL(entry.url);
    });
    this.#snapshots.clear();
    this.#warmPromise = null;
  }

  /**
   * @param {number} index
   */
  #primeAround(index) {
    this.#primeItem(index, 'high');
    this.#primeItem(index - 1, 'low');
    this.#primeItem(index + 1, 'low');
  }

  /**
   * @param {number} index
   * @param {'high' | 'low'} priority
   */
  #primeItem(index, priority) {
    const item = this.#items[index];
    if (!item) return;
    this.#hydratePhoto(item);
    this.#applySnapshot(item);
    item.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      img.loading = 'eager';
      img.fetchPriority = priority;
    });
  }

  /**
   * @param {HTMLElement} item
   */
  #hydratePhoto(item) {
    if (!item.classList.contains('product-media-container--lightbox-photo')) return;
    const lightboxImg = item.querySelector('img.product-media__image');
    if (!(lightboxImg instanceof HTMLImageElement) || lightboxImg.dataset.placeholderApplied === 'true') return;

    const slideIndex = item.dataset.slideIndex;
    const gallery = this.closest('media-gallery');
    const galleryImg = gallery?.querySelector(`slideshow-slide[data-slide-index="${slideIndex}"] img.product-media__image`);
    if (!(galleryImg instanceof HTMLImageElement) || !galleryImg.currentSrc) return;

    if (!lightboxImg.dataset.fullSrcset) {
      lightboxImg.dataset.fullSrcset = lightboxImg.getAttribute('srcset') || '';
    }
    lightboxImg.removeAttribute('srcset');
    lightboxImg.src = galleryImg.currentSrc;
    lightboxImg.dataset.placeholderApplied = 'true';

    const srcset = lightboxImg.dataset.fullSrcset;
    if (!srcset) return;

    // Setting srcset straight away would abort the placeholder and leave a gray box until the large file arrives.
    swapWhenLoaded(lightboxImg, { srcset });
  }

  /**
   * @param {HTMLElement | undefined} item
   * @param {number} timeout
   * @returns {Promise<boolean>}
   */
  async #whenItemReady(item, timeout) {
    if (!item) return false;
    const images = Array.from(item.querySelectorAll('img')).filter(
      (img) => img instanceof HTMLImageElement && !img.hidden && !img.closest('[hidden]')
    );
    if (!images.length) return true;
    const isReady = () => images.every((img) => img instanceof HTMLImageElement && img.complete && img.naturalWidth > 0);
    if (isReady()) return true;

    const decodes = images.map((img) =>
      img instanceof HTMLImageElement && typeof img.decode === 'function' ? img.decode().catch(() => {}) : Promise.resolve()
    );
    await Promise.race([Promise.all(decodes), new Promise((resolve) => setTimeout(resolve, timeout))]);
    return isReady();
  }

  /**
   * Shows the slide's spinner until every rendered image in it has loaded.
   * @param {HTMLElement | undefined} item
   */
  #watchItemLoad(item) {
    this.#loadWatch?.abort();
    this.#loadWatch = null;
    if (!this.#useExclusiveItems || !item) return;

    const snapshotShown = item.classList.contains('has-snapshot');
    const pending = Array.from(item.querySelectorAll('img')).filter((img) => {
      if (!(img instanceof HTMLImageElement)) return false;
      if (img.hidden || img.closest('[hidden]')) return false;
      if (snapshotShown && img.closest('.lightbox-live-fallback')) return false;
      if (!img.src && !img.getAttribute('srcset')) return false;
      return !img.complete || img.naturalWidth === 0;
    });
    if (!pending.length) {
      this.#setItemLoading(item, false);
      return;
    }

    const controller = new AbortController();
    this.#loadWatch = controller;
    const { signal } = controller;
    this.#setItemLoading(item, true);

    const loads = pending.map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true, signal });
          img.addEventListener('error', resolve, { once: true, signal });
        })
    );
    const timeout = new Promise((resolve) => setTimeout(resolve, LOAD_WATCH_TIMEOUT));

    Promise.race([Promise.all(loads), timeout]).then(async () => {
      if (signal.aborted) return;
      await Promise.all(pending.map((img) => this.#whenDecoded(img)));
      if (signal.aborted) return;
      this.#loadWatch = null;
      this.#setItemLoading(item, false);
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} loading
   */
  #setItemLoading(item, loading) {
    item.classList.toggle('is-loading', loading);
    if (loading) item.setAttribute('aria-busy', 'true');
    else item.removeAttribute('aria-busy');
    const overlay = item.querySelector('.lightbox-loading');
    if (overlay instanceof HTMLElement) overlay.hidden = !loading;
  }

  /**
   * @returns {import('./drag-zoom-wrapper').DragZoomWrapper | null}
   */
  #currentWrapper() {
    const item = this.#items[this.#activeIndex];
    const wrapper = item?.querySelector('drag-zoom-wrapper');
    return wrapper || null;
  }

  /**
   * @param {HTMLElement} element
   * @param {number} fallback
   */
  #slideIndexFor(element, fallback) {
    const value = element?.dataset?.slideIndex;
    const parsed = value != null ? Number(value) : NaN;
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleSwipeStart = (event) => {
    if (!this.#useExclusiveItems) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const wrapper = this.#currentWrapper();
    if (wrapper && wrapper.scale > 1.05) return;
    this.#swipeTracking = true;
    this.#swipeStartX = touch.clientX;
    this.#swipeStartY = touch.clientY;
  };

  /**
   * @param {TouchEvent} event
   */
  #handleSwipeEnd = (event) => {
    if (!this.#swipeTracking) return;
    this.#swipeTracking = false;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const wrapper = this.#currentWrapper();
    if (wrapper && wrapper.scale > 1.05) return;

    const dx = touch.clientX - this.#swipeStartX;
    const dy = touch.clientY - this.#swipeStartY;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;

    if (dx < 0) this.next();
    else this.previous();
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Loads a new source off-DOM and applies it only once decoded, so the visible image never blanks.
 * @param {HTMLImageElement} img
 * @param {{ src?: string, srcset?: string, sizes?: string }} source
 */
async function swapWhenLoaded(img, source) {
  const sizes = source.sizes || img.getAttribute('sizes') || '';
  const loader = new Image();
  if (sizes) loader.sizes = sizes;
  if (source.srcset) loader.srcset = source.srcset;
  if (source.src) loader.src = source.src;
  img.dataset.upgrading = 'true';

  try {
    await loader.decode();
  } catch {
    // Keep the current source if the larger file fails.
    delete img.dataset.upgrading;
    return;
  }

  if (source.sizes && parseFloat(source.sizes) >= (parseFloat(img.sizes) || 0)) img.sizes = source.sizes;
  if (source.srcset) img.srcset = source.srcset;
  if (source.src) img.src = source.src;
  delete img.dataset.upgrading;
}

/**
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement>}
 */
function getMostVisibleElement(elements) {
  const active = elements.find((el) => el.classList.contains('is-active'));
  if (active) return Promise.resolve(active);

  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
